import { Theme } from '@infotech-soe/erp-ui-kit';
import { createUseStyles } from 'react-jss';

export const DEFAULT_FONT_SIZE = 13;

export default createUseStyles((theme: Theme) => {
  const {
    secondary,
    borderActiveCell,
    backgroundGroup,
    backgroundGridPrimary,
    typoSecondary,
    typoCommon,
    typoCaption,
    surface,
    borderGroup,
    gridCheckbox,
  } = theme.colors;
  const { border } = theme.sizes.grid;
  const internalGrids = theme.sizes.gridInternalGrids;
  const isDark = theme.colorScheme === 'dark';

  const CELL_PADDING = '0 8px';

  const gridBorder = {
    color: borderGroup,
    style: border?.style,
    width: border?.width,
  };

  return {
    '@global': {
      '.grid': {
        '&__popover': {
          minWidth: 380,
          padding: 5,
          '&-buttons': { display: 'flex', alignItems: 'center' },
          '&-button': { width: '100%' },
          '&-button:not(:last-child)': { marginRight: 5 },
          '&-title': {
            textAlign: 'center',
            margin: '5px 0',
            color: typoCaption,
            fontWeight: 'bold',
          },
          '&-range': {
            display: 'flex',
            alignItems: 'center',
          },
          '&-content': {
            padding: '5px 0 10px 0',
            '& label': {
              margin: '0 5px',
            },
            '& [class*="Button"]': {
              color: secondary,
            },
          },
          '&-checkbox-group': {
            maxHeight: 200,
            overflow: 'auto',
          },
          '&-checkbox:not(:last-child)': { marginBottom: 5 },
        },
      },
      '.grid__wrapper': {
        position: 'relative',
        overflow: 'hidden',
        '&:after': {
          content: '',
          position: 'sticky',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        },
      },
      '.grid__search-input-wrapper': {
        position: 'absolute',
        top: 1,
        '& > *': {
          width: 'calc(100% - 2px) !important',
          margin: '0 1px',
          boxSizing: 'border-box',
        },
      },
      '.ReactVirtualized__Grid:focus': { outline: 'none' },
      '.hidden-cell, .hidden-cell-header': {
        padding: CELL_PADDING,
        fontSize: `${DEFAULT_FONT_SIZE}px`,
        display: 'inline-block',
        position: 'absolute',
        top: '-100px',
      },
      '.hidden-cell-header': { fontWeight: 'bold' },
    },
    agThemas: {
      backgroundColor: backgroundGroup,
      color: typoCommon,
      fontWeight: theme.typography.grid.fontWeight,
      fontSize: DEFAULT_FONT_SIZE,
      height: '100%',
      width: '100%',
      '&:focus': { outline: 'none' },
      '& .grid': {
        '&__bottom-left': {
          background: surface,
          borderRight: {
            width: internalGrids.borderWidth,
            style: internalGrids.borderStyle,
            color: borderGroup,
          },
        },
        '&__top-right': {
          background: surface,
          borderBottom: {
            width: internalGrids.borderWidth,
            style: internalGrids.borderStyle,
            color: borderGroup,
          },
        },
        '&__top-left': {
          background: surface,
          borderBottom: {
            width: internalGrids.borderWidth,
            style: internalGrids.borderStyle,
            color: borderGroup,
          },
          borderRight: {
            width: internalGrids.borderWidth,
            style: internalGrids.borderStyle,
            color: borderGroup,
          },
        },
        '&__resize-line': {
          position: 'fixed',
          width: '3px',
          height: '30px',
          background: borderGroup,
          display: 'none',
          zIndex: '100',
          cursor: 'col-resize',
        },
        '&__cell-checkbox, &__header-checkbox': {
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          justifyContent: 'center',
        },
        '&__checkbox-icon': { color: gridCheckbox },
        '&__cell': {
          lineHeight: '30px',
          textAlign: 'left',
          position: 'relative',
          height: '100%',
          '&-value': {
            padding: CELL_PADDING,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            height: '100%',
            userSelect: 'none',
          },
          '&--odd': { background: backgroundGroup },
          '&--even': { background: backgroundGridPrimary },
          '&--active': {
            backgroundColor: secondary,
            color: typoSecondary,

            '& .grid__totals': {
              color: typoCaption,
            },
          },
          '&--focused': {
            outlineOffset: `-2px`,
            outline: `2px dotted ${borderActiveCell}`,
          },
          '&:focus': { outline: `none` },
          '&--text-right': { textAlign: 'right' },
          '&-input': {
            display: 'flex',
            alignItems: 'center',
            '& div:focus-within': {
              borderColor: secondary,
            },
          },
        },
        '&__header': {
          '&-icons': {
            display: 'flex',
            alignItems: 'center',
          },
          '&-dnd-cell': {
            lineHeight: '30px',
            textAlign: 'left',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            padding: CELL_PADDING,
            '&--cursor': { cursor: 'auto' },
          },
          '&-cell': {
            background: surface,
            color: typoCaption,
            '&--dragover': {
              outlineOffset: '-3px',
              outline: `3px dotted #000`,
            },
            // TODO unused
            '&--filter:after': {
              content: '""',
              position: 'absolute',
              borderBottom: '3px solid',
              height: 0,
              left: 0,
              top: 0,
              width: '100%',
            },
          },
          '&-cell-title': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexGrow: 1,
            '&.grid__header-cell-editable': {
              fontStyle: 'italic',
              paddingRight: 2,
            },
          },
        },
        '&__resize-button': {
          background: 'transparent',
          border: 0,
          cursor: 'col-resize',
          position: 'absolute',
          height: '100%',
          padding: '0 10px',
          right: '-9px',
          top: '0',
          '&:active:after': { borderRightWidth: '3px' },
          '&:focus': { outline: 'none' },
          '&:after': {
            content: '""',
            position: 'absolute',
            borderRight: `1px solid ${borderGroup}`,
            height: '100%',
            top: '0',
            width: '1px',
            left: '50%',
          },
          '&[disabled]': { cursor: 'default' },
        },
        '&__sort': {
          '&-info': {
            display: 'flex',
            alignItems: 'center',
          },
          '&-index': {
            fontSize: '0.8em',
            height: '23px',
          },
          '&-icon--desc': { transform: 'rotate(180deg)' },
        },
        '&__search-btn': {
          alignSelf: 'center',
          flexShrink: 0,
          height: 20,
          width: 20,
          margin: '0 2px',
          '&:last-child': { marginRight: 6 },
          padding: 0,
          border: 'none',
          borderRadius: 3,
          background: 'transparent',
          color: typoCaption,
          fontSize: DEFAULT_FONT_SIZE,
          cursor: 'pointer',
          fontFamily: 'inherit',
          lineHeight: 1,
          '&:hover': { color: 'var(--primaryHover)' },
          '&:disabled, &--disabled': {
            opacity: 0.35,
            cursor: 'default',
            '&:hover': { color: typoCaption },
          },
          '&--active': {
            background: isDark ? typoSecondary : secondary,
            color: isDark ? secondary : typoSecondary,
            '&:hover': {
              background: isDark
                ? 'var(--primaryHover)'
                : 'var(--secondaryHover)',
              color: isDark ? typoCommon : typoSecondary,
            },
          },
        },
        '&__search-counter': {
          alignSelf: 'center',
          flexShrink: 0,
          minWidth: 42,
          padding: '0 6px 0 4px',
          marginRight: 2,
          borderRight: `1px solid ${borderGroup}`,
          textAlign: 'right',
          color: typoCaption,
          fontSize: DEFAULT_FONT_SIZE,
          lineHeight: 1,
        },
        '&__aside': {
          display: 'flex',
          justifyContent: 'start',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          paddingLeft: '11px',
          '&-checkbox-wrapper': {
            display: 'flex',
            justifyContent: 'start',
            width: '100%',
            height: '100%',
            paddingLeft: '11px',
          },
          '&-label-wrapper': {
            display: 'flex',
            width: '100%',
            height: '100%',
            justifyContent: 'start',
            alignItems: 'center',
            paddingLeft: '5px',
          },
        },
        '&__aside-label': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'start',
          width: '100%',
          height: '100%',
          color: typoCaption,
          fontWeight: 'bold',
          paddingLeft: '5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '&__totals': {
          backgroundColor: backgroundGroup,
          color: typoCaption,
          fontWeight: 'bold',
        },
      },
    },
    gridBorderBottom: {
      borderBottom: gridBorder,
    },
    gridBorderLeft: {
      borderLeft: gridBorder,
    },
    gridBorderRight: {
      borderRight: gridBorder,
    },
    gridBorderTop: {
      borderTop: gridBorder,
    },
    gridBorderAll: {
      composes: [
        '$gridBorderBottom',
        '$gridBorderLeft',
        '$gridBorderRight',
        '$gridBorderTop',
      ],
    },
    toast: {
      flex: 'auto',
    },
  };
});
